import { useLazyQuery, useQuery } from "@apollo/client";
import { capitaliseFirstLetter, capitaliseFirstLetterOfEachWord } from "@shared/translations/src";
import { getRouteApi } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { useCallback, useEffect } from "react";
import { SUPPLY_CHAIN_PARTNER_ROLES } from "../ContactForm/validation";
import { FilterBadges } from "../ContactsFilterBadges";
import { SearchFilter } from "../ContactsSearch";
import { Combobox } from "@/components/Combobox";
import { useTableSorting } from "@/components/DataTable/useTableSorting";
import { FilterSheet } from "@/components/FilterSheet";
import { Pagination } from "@/components/Pagination";
import { pageInfoFragment, usePagination } from "@/components/Pagination/usePagination";
import { Table } from "@/components/Table";
import { Label } from "@/components/ui/label";
import { ToastAction } from "@/components/ui/toast";
import { getRoleOptions, getTypeOptions, supplyChainPartnerTypesQuery } from "@/contacts/ContactForm";
import { type ContactFilters } from "@/contacts/schemas/contactFilterSchema";
import { graphql, type VariablesOf, type ResultOf } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { useToast } from "@/hooks/use-toast";
import { useFilterSheet } from "@/hooks/useFilterSheet";
import { removeNothings } from "@/lib/utils/arrays";
import { getErrorMessage } from "@/lib/utils/errors";
import {
  formatEmptyableValue,
  formatInteger,
  formatName,
  formatNullableValue,
  formatPhoneNumber,
} from "@/lib/utils/formatting";

const routePath = "/(authed)/_layout/contacts";
const { accessor } = createColumnHelper<SupplyChainPartner>();
const routeApi = getRouteApi(routePath);

type SortingField = NonNullable<NonNullable<VariablesOf<typeof supplyChainPartnersQuery>["sortBy"]>[0]>["field"];

interface ContactsTableProps {
  filters: ContactFilters;
  onUpdateFilters: (newFilters: ContactFilters) => void;
}

export function ContactsTable({ filters, onUpdateFilters }: ContactsTableProps) {
  const navigate = routeApi.useNavigate();

  const { toast } = useToast();
  const { activeBusinessUnitId } = useActiveBusinessUnit();
  const { roles, types, search } = routeApi.useSearch();

  const { filterButton, filterSheetProps, localFilters, setLocalFilters } = useFilterSheet({
    initialFilters: filters,
    onApply: (newFilters, cleanedFilters) => {
      onUpdateFilters(newFilters);
      pagination.controls.handleResetPagination();
      void navigate({
        search: (prev) => ({
          ...prev,
          roles: cleanedFilters.roles ?? undefined,
          types: cleanedFilters.types ?? undefined,
        }),
      });
    },
    getActiveCount: (f) => (f.roles?.length || 0) + (f.types?.length || 0),
  });

  const [getContacts, { loading, data, error, refetch }] = useLazyQuery(supplyChainPartnersQuery, {
    fetchPolicy: "cache-and-network",
  });

  const pagination = usePagination({
    totalCount: data?.supplyChainPartners?.numSupplyChainPartners || 0,
    pageInfo: data?.supplyChainPartners?.pageInfo,
    loading,
  });

  const { onSortingChange, sorting, sortBy } = useTableSorting<SortingField>({ id: "UPDATED_AT", desc: true });

  const contacts = removeNothings(
    data?.supplyChainPartners?.edges.map((edge) => {
      return edge?.node;
    }) || [],
  );

  const { data: typesData } = useQuery(supplyChainPartnerTypesQuery);
  const roleOptions = getRoleOptions(SUPPLY_CHAIN_PARTNER_ROLES);
  const supplyChainPartnerTypeOptions = getTypeOptions(typesData?.supplyChainPartnerTypes?.edges || []);

  useEffect(() => {
    const variables: VariablesOf<typeof supplyChainPartnersQuery> = {
      first: pagination.paginationQueryVariables.first,
      last: pagination.paginationQueryVariables.last,
      after: pagination.paginationQueryVariables.after,
      before: pagination.paginationQueryVariables.before,
      sortBy,
      status: ["ACTIVE"],
      businessUnitIds: activeBusinessUnitId ? [activeBusinessUnitId] : undefined,
      types: types,
      roles: roles,
      search,
    };

    const fetchData = async () => {
      await getContacts({
        variables,
      });
    };

    fetchData().catch((error: unknown) => {
      toast({
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        description: getErrorMessage(error) || "There was a problem with your request.",
        action: (
          <ToastAction onClick={() => refetch()} altText="Try again">
            Try again
          </ToastAction>
        ),
      });
    });
  }, [
    pagination.paginationQueryVariables.first,
    pagination.paginationQueryVariables.last,
    pagination.paginationQueryVariables.after,
    pagination.paginationQueryVariables.before,
    getContacts,
    toast,
    refetch,
    activeBusinessUnitId,
    sortBy,
    roles,
    types,
    search,
  ]);

  const handleContactClick = useCallback(
    (contact: SupplyChainPartner) => {
      if (!contact) return;

      void navigate({
        to: "/contacts/$contactId",
        params: { contactId: contact.id },
      });
    },
    [navigate],
  );

  return (
    <>
      <Table.Root
        data={contacts}
        columns={columns}
        loading={loading && !data}
        error={error?.message}
        tableId="contactsTable"
        sortConfig={{ onSortingChange, sorting }}
      >
        <Table.Header>
          <SearchFilter handleResetPagination={pagination.controls.handleResetPagination} />
          <Table.ColumnToggle />
          <Table.Filters {...filterButton} />
        </Table.Header>

        <div className="px-4 pb-2 min-h-8">
          <FilterBadges
            filters={filters}
            onUpdateFilters={onUpdateFilters}
            handleResetPagination={pagination.controls.handleResetPagination}
            roleOptions={roleOptions}
            typeOptions={supplyChainPartnerTypeOptions}
          />
        </div>

        <Table.Body onRowClick={handleContactClick} pageSize={Number(pagination.paginationInfo.pageSize)} />

        <Table.Footer className="flex justify-end">
          <Pagination pagination={pagination} />
        </Table.Footer>
      </Table.Root>

      <FilterSheet {...filterSheetProps}>
        <div className="flex flex-col gap-2">
          <Label htmlFor="role-filter">Role</Label>
          <Combobox
            id="role-filter"
            type="multi"
            options={roleOptions}
            selected={localFilters.roles || []}
            onChange={(roles) => {
              setLocalFilters({ ...localFilters, roles });
            }}
            placeholder="Select role"
            isModal
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="type-filter">Operation Type</Label>
          <Combobox
            id="type-filter"
            type="multi"
            options={supplyChainPartnerTypeOptions}
            selected={localFilters.types || []}
            onChange={(types) => {
              setLocalFilters({ ...localFilters, types });
            }}
            placeholder="Select operation type"
            isModal
          />
        </div>
      </FilterSheet>
    </>
  );
}

const columns = [
  accessor((row) => row?.businessName, {
    id: "BUSINESS_NAME" satisfies SortingField,
    header: "Business Name",
    cell: (info) => info.getValue(),
    enableSorting: true,
  }),
  accessor((row) => row?.primaryContact, {
    id: "contactName",
    header: "Principal Contact Name",
    cell: (info) => {
      const contact = info.getValue();
      return formatName({
        firstName: contact?.firstName,
        lastName: contact?.lastName,
      });
    },
  }),
  accessor((row) => row?.businessAddress, {
    id: "city",
    header: "City",
    cell: (info) => {
      const address = info.getValue();
      return formatEmptyableValue(address?.city);
    },
    enableSorting: false,
  }),
  accessor((row) => row?.businessAddress, {
    id: "state",
    header: "State",
    cell: (info) => {
      const address = info.getValue();
      return formatEmptyableValue(address?.state);
    },
    enableSorting: false,
  }),
  accessor((row) => row?.primaryContact, {
    id: "contactNumber",
    header: "Phone Number",
    cell: (info) => {
      const contact = info.getValue();
      return formatPhoneNumber(contact?.phone);
    },
  }),
  accessor((row) => row?.primaryContact, {
    id: "contactEmail",
    header: "Email",
    cell: (info) => {
      const contact = info.getValue();
      return formatEmptyableValue(contact?.email);
    },
  }),
  accessor((row) => row?.type, {
    id: "TYPE" satisfies SortingField,
    header: "Type",
    cell: ({ row }) => capitaliseFirstLetter(formatNullableValue(row.original?.type?.label)),
    enableSorting: true,
  }),
  accessor((row) => row?.accountNumber, {
    id: "ACCOUNT_NUMBER" satisfies SortingField,
    header: "Account Number",
    cell: ({ row }) => formatEmptyableValue(row.original?.accountNumber),
    enableSorting: true,
  }),
  accessor((row) => row?.role, {
    id: "ROLE" satisfies SortingField,
    header: "Role",
    cell: ({ row }) =>
      row.original?.role ? capitaliseFirstLetterOfEachWord(row.original?.role.toLowerCase().replace("_", " ")) : "—",
    enableSorting: true,
  }),
  accessor((row) => row?.monthlyTarget, {
    id: "MONTHLY_TARGET",
    header: "Monthly Target",
    cell: ({ row }) => formatInteger(row.original?.monthlyTarget),
    enableSorting: false,
  }),
];

export const supplyChainPartnersQuery = graphql(
  `
    query SupplyChainPartners(
      $first: Int
      $last: Int
      $before: String
      $after: String
      $status: [SupplyChainPartnerStatusEnumType!]
      $sortBy: [SupplyChainPartnersColumnSort]
      $businessUnitIds: [ID!]
      $types: [ID!]
      $roles: [SupplyChainPartnerRoleEnumType!]
      $search: String
    ) {
      supplyChainPartners(
        first: $first
        last: $last
        before: $before
        after: $after
        sortBy: $sortBy
        owners: $businessUnitIds
        status: $status
        types: $types
        roles: $roles
        search: $search
      ) {
        numSupplyChainPartners
        pageInfo {
          ...PageInfo
        }
        edges {
          node {
            id
            role
            type {
              id
              label
            }
            accountNumber
            businessName
            businessAddress {
              city
              state
            }
            primaryContact {
              id
              firstName
              lastName
              email
              phone
            }
            status
            monthlyTarget
          }
        }
      }
    }
  `,
  [pageInfoFragment],
);

export type SupplyChainPartner = NonNullable<
  NonNullable<NonNullable<ResultOf<typeof supplyChainPartnersQuery>["supplyChainPartners"]>["edges"]>[number]
>["node"];

export const createSupplyChainPartnerMutation = graphql(`
  mutation CreateSupplyChainPartner($input: CreateSupplyChainPartnerInput!) {
    createSupplyChainPartner(input: $input) {
      supplyChainPartner {
        id
        businessAddress {
          id
          line1
          line2
          city
          postcode
          state
          country {
            id
            name
          }
        }
        primaryContact {
          id
          firstName
          lastName
          email
          phone
        }
        businessName
        role
        status
        accountNumber
        monthlyTarget
        managedBy {
          id
          firstName
          lastName
          email
        }
        type {
          id
          label
        }
      }
    }
  }
`);
